// UI
import { amber } from "@mui/material/colors";
import { Check, Close, InfoOutlined, Lens } from "@mui/icons-material";
import { Box, Stack, Theme, Tooltip, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { SafeLeaseAvatarWithText } from "@safelease/components";
import { GridRenderCellParams } from "@mui/x-data-grid";

// Data
import { Message_Reputation as Message } from "@safelease/service-utilities";

// State / hooks
import { useReputation } from "../../../../useReputation";

// Utils
import _ from "lodash";
import dayjs from "dayjs";

export const contactGridColumns: GridColDef[] = [
  {
    field: "contact",
    headerName: "Contact",
    disableColumnMenu: true,
    renderHeader: () => (
      <Stack alignItems="center" spacing={1} direction="row">
        <Typography>Contact</Typography>
        <Tooltip title="Current or former tenant name">
          <InfoOutlined
            sx={{
              height: 18,
              color: (theme: Theme) => theme.palette.grey.A100,
            }}
          />
        </Tooltip>
      </Stack>
    ),
    flex: 1,
    valueGetter: ({ row }) => row.name,
    renderCell: ({ value }) => <SafeLeaseAvatarWithText text={value} />,
  },
  {
    field: "review",
    headerName: "Review",
    disableColumnMenu: true,
    flex: 1,
    renderCell: ({ value }) => {
      return (
        <Box>
          {/* User has not left a review */}
          {!value && <Typography variant="body2">-</Typography>}

          {/* User has left a review, pending reply */}
          {value && !value.responseSnippet && (
            <Typography variant="body2">
              <Lens sx={{ height: 18, width: 18, color: amber[400], mr: 1 }} />
              Pending Response
            </Typography>
          )}

          {/* User has left a review, and the company has replied*/}
          {value && value.responseSnippet && (
            <Typography variant="body2" color="grey.A100">
              <Check sx={{ mr: 1, color: (theme: Theme) => theme.palette.green.main }} />
              Responded
            </Typography>
          )}
        </Box>
      );
    },
  },
  {
    field: "feedback",
    headerName: "Private Review",
    disableColumnMenu: true,
    sortable: false,
    flex: 1,
    renderCell: ({ value }) => {
      return (
        <Box>
          {/* User has not left a review */}
          {!value && <Typography variant="body2">-</Typography>}

          {/* User has left a review, pending reply */}
          {value && !value.responseSnippet && (
            <Typography variant="body2">
              <Lens sx={{ height: 18, width: 18, color: amber[400], mr: 1 }} />
              Pending Response
            </Typography>
          )}

          {/* User has left a review, and the company has replied*/}
          {value && value.responseSnippet && (
            <Typography variant="body2" color="grey.A100">
              <Check sx={{ mr: 1, color: (theme: Theme) => theme.palette.green.main }} />
              Responded
            </Typography>
          )}
        </Box>
      );
    },
  },
  {
    field: "messages",
    headerName: "Contact History",
    disableColumnMenu: true,
    sortable: true,
    flex: 1,
    valueGetter: ({ value }: { value?: Array<Message> }) => value?.length ?? 0,
    renderCell: (params: GridRenderCellParams) => {
      const messageCount = params.value as number;
      return <Typography variant="body2">{messageCount} messages sent</Typography>;
    },
  },
  {
    field: "lastContactedAt",
    headerName: "Last Contacted At",
    disableColumnMenu: true,
    flex: 1,
    valueGetter: ({ row }) => {
      if (row.messages.length === 0) return null;
      return Math.max(...row.messages.map((message) => new Date(message.sentAt).getTime()));
    },
    renderCell: ({ value }) => {
      if (value === null) return "-";
      return dayjs(value).format("MMM DD, hh:mm");
    },
    sortComparator: (v1, v2) => {
      if (v1 === null && v2 === null) return 0;
      if (v1 === null) return -1;
      if (v2 === null) return 1;
      return v1 - v2;
    },
  },

  {
    field: "unsubscribeGroupMemberships",
    headerName: "Campaign Subscriptions",
    disableColumnMenu: true,
    flex: 1,
    renderCell: ({ value, row }) => {
      const { portfolio } = useReputation();

      // Unsubscribe groups the user is a part of
      const userUnsubscribeGroupMemberships = value.map((membership) => membership.unsubscribeGroupId);

      // Subscription status
      const manuallyOptedOut = row.manualOptOut;
      const fullySubscribed = userUnsubscribeGroupMemberships?.length === 0;
      const partiallySubscribed =
        userUnsubscribeGroupMemberships?.length > 0 && userUnsubscribeGroupMemberships?.length < portfolio.unsubscribeGroups?.length;
      const unsubscribed =
        userUnsubscribeGroupMemberships?.length === portfolio.unsubscribeGroups?.length && userUnsubscribeGroupMemberships?.length > 0;

      if (manuallyOptedOut) {
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Close sx={{ color: "red.main" }} />
            <Typography component="span">Manually Opted Out</Typography>
          </Stack>
        );
      }

      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          {fullySubscribed && <Check sx={{ color: "green.main" }} />}
          {fullySubscribed && <Typography component="span">Fully Subscribed</Typography>}
          {partiallySubscribed && <Lens sx={{ color: amber[400] }} />}
          {partiallySubscribed && <Typography component="span">Partially Subscribed</Typography>}
          {unsubscribed && <Close sx={{ color: "red.main" }} />}
          {unsubscribed && <Typography component="span">Unsubscribed</Typography>}
        </Stack>
      );
    },
  },
];
